import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { DEFAULT_STARTUP_POLICY, type StartupPolicySettings } from "@evolution/shared";
import { resolveApiEndpoint } from "../api/client";
import { validStartupPolicy } from "./startup-policy-validation";
import { usesNativeContent } from "./native-content-update";
import { assertArenaServerCompatibility } from "./server-compatibility";
type Phase = "connecting" | "checking" | "downloading" | "verifying" | "applying" | "ready" | "offline" | "unavailable" | "fallback";
export interface StartupUpdateSnapshot { phase: Phase; received: number; total: number }
interface StartupPlugin {
  startupUpdate(policy: StartupPolicySettings): Promise<{ status: string }>;
  addListener(name: "startupUpdateProgress", listener: (value: { stage: string; received: number; total: number }) => void): Promise<PluginListenerHandle>;
}
const content = registerPlugin<StartupPlugin>("VarnaxContent");
let preparation: Promise<void> | undefined;
let snapshot: StartupUpdateSnapshot = { phase: "connecting", received: 0, total: 0 };
const listeners = new Set<() => void>();
function publish(next: StartupUpdateSnapshot) { snapshot = next; listeners.forEach((listener) => listener()); }
export const startupUpdates = { getSnapshot: () => snapshot, subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; } };
/** The update service is optional; compatible online play is not. No policy or
 * unverified content is trusted on this fallback path. */
async function compatibleGameServiceReachable(): Promise<boolean> {
  const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 4000);
  try {
    const response = await fetch(resolveApiEndpoint("/api/meta"), { credentials: "omit", cache: "no-store", redirect: "error", signal: controller.signal });
    if (!response.ok) return false;
    const meta: unknown = await response.json();
    assertArenaServerCompatibility(meta);
    return !!meta && typeof meta === "object" && "maintenance" in meta && meta.maintenance === false;
  } catch { return false; }
  finally { clearTimeout(timeout); }
}
/** Real first-party reachability, not navigator.onLine alone. Never download during a match. */
export function prepareStartupUpdate(): Promise<void> {
  // Every caller in this document shares the same native gate. In particular,
  // mounting/loading UI twice must not let a native "busy/current" response
  // commit an old menu while the first request is still activating a bundle.
  if (preparation) return preparation;
  const attempt = runStartupUpdate();
  preparation = attempt;
  void attempt.catch(() => { if (preparation === attempt) preparation = undefined; });
  return attempt;
}

async function runStartupUpdate(): Promise<void> {
  publish({ phase: "connecting", received: 0, total: 0 });
  if (!Capacitor.isNativePlatform()) { publish({ phase: "ready", received: 0, total: 0 }); return; }
  if (!navigator.onLine) { publish({ phase: "offline", received: 0, total: 0 }); throw Error("Offline"); }
  const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
  let policy = DEFAULT_STARTUP_POLICY;
  try {
    const response = await fetch(resolveApiEndpoint("/api/startup-policy"), { credentials: "omit", cache: "no-store", signal: controller.signal });
    if (!response.ok) throw Error("Startup service unavailable");
    const value = await response.json() as { config?: unknown };
    if (!validStartupPolicy(value.config)) throw Error("Startup policy unavailable");
    policy = value.config;
  } catch {
    clearTimeout(timeout);
    if (await compatibleGameServiceReachable()) {
      publish({ phase: "fallback", received: 0, total: 0 });
      return;
    }
    publish({ phase: "unavailable", received: 0, total: 0 }); throw Error("Internet or startup service unavailable");
  } finally { clearTimeout(timeout); }
  if (!usesNativeContent() || !policy.enabled) { publish({ phase: "ready", received: 0, total: 0 }); return; }
  let handle: PluginListenerHandle | undefined;
  try {
    publish({ phase: "checking", received: 0, total: 0 });
    handle = await content.addListener("startupUpdateProgress", (value) => {
      if (!["checking", "downloading", "verifying"].includes(value.stage) || !Number.isFinite(value.received) || !Number.isFinite(value.total)
        || value.received < 0 || value.total < value.received) return;
      publish({ phase: value.stage as Phase, received: value.received, total: value.total });
    });
    const result = await content.startupUpdate(policy);
    if (result.status === "applying") {
      publish({ phase: "applying", received: 1, total: 1 });
      // Native switches the verified asset root. Keep the startup screen until
      // the new document starts; never flash the old menu in between.
      await new Promise<void>(() => undefined);
    }
    publish({ phase: result.status === "offline" || result.status === "incompatible" ? "fallback" : "ready", received: 0, total: 0 });
  } catch {
    // Older AABs have no startup method. Their signed bundled UI remains usable.
    publish({ phase: "fallback", received: 0, total: 0 });
  } finally { await handle?.remove().catch(() => undefined); }
}
